import { createSelector } from '@reduxjs/toolkit';
import { ECustomerSource } from 'core/model/enums/customer.enum';
import { TDistributorSource } from 'core/model/interfaces/checkout.interface';
import { authCustomerIsHybridSelector, authSelector } from 'core/store/auth/selectors';
import { isFill } from 'shared/utils/string.utils';
import {
  checkoutDetailSelector,
  checkoutShippingSelector,
  paymentMethodSelector,
  selectedDeliveryDatesSelector,
} from './core.selectors';
import { hasCreditsSelector } from './credits.selectors';

export const shippingDeliveryDatesSelector = createSelector(
  checkoutShippingSelector,
  shipping => shipping.deliveryDates,
);

export const checkoutDeletedProductsSelector = createSelector(checkoutDetailSelector, detail => detail.deletedProducts);

export const hasEmptyDeliveryDatesSelector = createSelector(selectedDeliveryDatesSelector, deliveryDates => {
  const distributorDeliveryDates = Object.keys(deliveryDates) as Array<TDistributorSource>;
  const hasDeliveryDates = distributorDeliveryDates.some(
    distributor => Object.keys(deliveryDates[distributor]).length > 0,
  );
  return !hasDeliveryDates;
});

export const checkoutGroupingDatesSelector = createSelector(
  authCustomerIsHybridSelector,
  checkoutDetailSelector,
  (isHybrid, detail) => {
    const alicorpGroupingRules = detail.alicorpCart.groupingRule.products.length
      ? detail.alicorpCart.groupingRule
      : null;
    const alliedGroupingRules = detail.alliedCart.groupingRule.products.length ? detail.alliedCart.groupingRule : null;
    const generalGroupingRules = detail.generalCart.groupingRule.products.length
      ? detail.generalCart.groupingRule
      : null;

    if (!isHybrid) {
      return generalGroupingRules ? [generalGroupingRules] : [];
    }

    return [alicorpGroupingRules, alliedGroupingRules].flatMap(rule => rule || []);
  },
);

export const canEnableShippingSubmitSelector = createSelector(
  checkoutGroupingDatesSelector,
  paymentMethodSelector,
  hasCreditsSelector,
  authSelector,
  (groupingRules, paymentMethods, hasCredits, auth) => {
    const hasEmptyGroupingRules = groupingRules.length === 0;
    if (hasEmptyGroupingRules) return false;

    const sourceId = auth.customer.source;

    if (hasCredits) return true;

    if (sourceId === ECustomerSource.DEX || sourceId === ECustomerSource.APUDEX) {
      return isFill(paymentMethods.generalCart?.id);
    }

    return isFill(paymentMethods.alicorpCart?.id) || isFill(paymentMethods.alliedCart?.id);
  },
);

export const checkoutAllProductsSelector = createSelector(
  authCustomerIsHybridSelector,
  checkoutDetailSelector,
  (isHybrid, detail) => {
    if (!isHybrid) {
      return detail.generalCart.groupingRule?.products || [];
    }
    const alicorpProducts = detail.alicorpCart?.groupingRule?.products || [];
    const alliedProducts = detail.alliedCart?.groupingRule?.products || [];
    return [...alicorpProducts, ...alliedProducts];
  },
);
